import React, { useEffect, useState } from 'react';
import { useDidUpdate } from '../../Helpers/useDidUpdate';
import { useToasts } from 'react-toast-notifications';
import { Prompt } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faExclamationCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FuzzyDate, GetOptionText } from '../../Helpers/Helper';
import { makeCall } from '../../auth-utils';
import { api, globalErrorHandler } from '../../api/create-api';
import { Row, Col, Button, Nav, NavItem, NavLink, Label, ListGroup, ListGroupItem } from 'reactstrap';
import NumberFormat from 'react-number-format';
import { Field, CancelButton } from '@galop/component-library';
import Can from '../Shared/Can';
import { Program } from './Program';
import Lock, { AppIsLocked } from '../Shared/Lock';

export const View = (props) => {
    const ls = require('local-storage');
    const { addToast } = useToasts();
    const { selectedAsset, declarationStatuses, eraStatuses, evictionStatuses, reasons, eraPrograms, newResource, ReloadResource, HideDetails, MakeDirty, CleanUp, Close } = props;
    const [resource, setResource] = useState(props.resource || { assetId: selectedAsset.id, ERA: {} });
    const [resourceIsDirty, setResourceIsDirty] = useState(false);
    const [editResource, setEditResource] = useState(props.EditResource && !AppIsLocked());
    const [programId, setProgramId] = useState(null);
    const [savingData, setSavingData] = useState(false);
    const residentStatuses = [
        "Current",
        "Notice",
        "Past"
    ];
    const filingDateValidation = {
        minimumDate: ls('MinimumDate') || "9/1/2020",
        maximumDate: new Date(),
        requiredForStatuses: [
            "53af8de6-bf69-4c13-b840-47ca96b51dee",
            "73315300-813e-414c-a719-b74be6968ca7"
        ]
    };

    useDidUpdate(() => {
        if (resourceIsDirty) {
            window.onbeforeunload = () => true;
        } else {
            window.onbeforeunload = null;
        }
    });

    useEffect(() => {
        return () => {
            window.onbeforeunload = null;
        };
    }, []);

    useEffect(() => {
        if (props.resource) {
            setResource({ ...resource, eraPrograms: validateERAAmounts(resource.eraPrograms), id: props.resource.id });
        }
    }, [props.resource]);

    const updateResource = (field, value, resource, id) => {
        if (id) {
            /* Set Id */
            resource.id = id;
        }

        /* Set Value */
        if (value) {
            resource[field] = value;
        }
        else {
            delete resource[field];
        }

        /* field deletions */
        if (field === 'evictionStatusId' && !(GetOptionText(evictionStatuses, value, 'id', 'name') || '').includes('Unknown')) {
            delete resource.evictionSubstatusId;
        }

        if (field === 'evictionStatusId' && !value) {
            delete resource.evictionSubstatusId;
            delete resource.reasonId;
            delete resource.filingDate;
            delete resource.completionDate;
            delete resource.notes;
        }

        if (field === 'eraStatusId') {
            if (!value || !(GetOptionText(eraStatuses, value, 'id', 'name') || '').includes('Received')) {
                delete resource.eraReceivedAmount;
                delete resource.eraDateReceived;
            }
        }

        setResourceIsDirty(true);

        MakeDirty(resource);
    };

    const AllValid = () => {
        let isValid = true;
        isValid = isValid && !!resource.residentName;
        isValid = isValid && !!resource.unit;
        isValid = isValid && (!!resource.evictionStatusId || !!resource.declarationStatusId || (resource.eraPrograms && resource.eraPrograms.length > 0));

        isValid = isValid && (!(resource.filingDate || filingDateValidation.requiredForStatuses.includes(resource.evictionStatusId)) || (new Date(resource.filingDate) >= new Date(filingDateValidation.minimumDate) && new Date(resource.filingDate) <= new Date(filingDateValidation.maximumDate)));

        /* Validate ERA Program Ids */
        isValid = isValid && (!resource.eraPrograms || resource.eraPrograms.length === 0 || !(resource.eraPrograms.find(p => GetOptionText(eraPrograms, p.eraProgramId, 'id', 'deleted') || !(!!p.eraProgramId))));

        /* Validate ERA Program Dates Received */
        isValid = isValid && (!resource.eraPrograms || resource.eraPrograms.length === 0 || !(resource.eraPrograms.find(p => (GetOptionText(eraStatuses, p.eraStatusId, 'id', 'name') || '').includes('Received') && !p.eraDateReceived)));

        /* Validate ERA Program Amounts */
        isValid = isValid && (!resource.eraPrograms || resource.eraPrograms.length === 0 || !(resource.eraPrograms.find(p => p.error)));;

        return isValid;
    };

    const saveResource = (resourceId, callback) => {
        if (resourceIsDirty) {
            if (AllValid()) {
                makeCall(async (authHeader) => {
                    let method = 'put';
                    if (newResource) {
                        method = 'post';
                    }
                    setSavingData(true);
                    const result = await api({
                        url: `api/leases`,
                        method,
                        data: { ...resource, leaseId: parseInt(resource.leaseId) },
                        headers: authHeader
                    })
                        .catch(error => {
                            setSavingData(false);
                            globalErrorHandler(error, addToast);
                        });

                    if (result && result.status === 200) {
                        if (callback && typeof callback === 'function') {
                            callback();
                        }
                    }
                });
            }
            else {
                addToast('Please fix the invalid fields', { appearance: 'error', autoDismiss: true });
            }
        }
    };

    const deleteResource = (resourceId, callback) => {
        makeCall(async (authHeader) => {
            await api.delete(`api/leases/${resourceId}`, {
                headers: authHeader
            })
                .catch(error => globalErrorHandler(error, addToast));

            if (callback && typeof callback === 'function') {
                callback();
            }
        });
    };

    const addProgram = () => {
        setResource({ ...resource, eraPrograms: [...(resource.eraPrograms || []), {}] });
        setProgramId(null);
        setResourceIsDirty(true);
    };

    const updateProgram = (field, value, program, index) => {
        /* Set Value */
        if (value) {
            program[field] = value;
        }
        else {
            delete program[field];
        }

        /* field deletions */
        if (field === 'eraStatusId') {
            if (!value || !(GetOptionText(eraStatuses, value, 'id', 'name') || '').includes('Received')) {
                delete program.eraReceivedAmount;
                delete program.eraDateReceived;
            }
        }
        resource.eraPrograms.splice(index, 1, validateERAProgramAmounts(program));
        setResource({ ...resource, eraPrograms: resource.eraPrograms });
        setResourceIsDirty(true);
    };

    const validateERAProgramAmounts = (program) => {
        /* amount validation */
        if ((program.eraRequestedAmount && program.eraRequestedAmount > 100000) || (program.eraReceivedAmount && program.eraReceivedAmount > 100000)) {
            program.error = `The field, Amount ${program.eraRequestedAmount > 100000 ? 'Requested' : 'Received'}, has a value over $100,000.`;
        }
        else {
            delete program.error;
        }

        return program;
    };

    const validateERAAmounts = (programs) => {
        return programs.map(prog=> validateERAProgramAmounts(prog));
    };

    const removeProgram = (index) => {
        const newPrograms = [...resource.eraPrograms.filter((p, i) => i !== index)];

        if (newPrograms.length > 0) {
            console.log('save new programs');
            setResource({ ...resource, eraPrograms: newPrograms });
        }
        else {
            console.log('remove all programs');
            const { eraPrograms, ...newResouce } = resource;
            setResource({ ...newResouce });
        }
        setResourceIsDirty(true);
    };

    return (
        <div className={props.className}>
            <Prompt when={resourceIsDirty} message='You have unsaved changes, are you sure you want to leave?' />
            <Row className=" align-items-center">
                <Col sm="6">
                    <span className="h4">{resource.residentName}</span> <span className="text-dark">{resource.unit} {resource.leaseId ? <span>(#{resource.leaseId})</span> : null}</span>
                </Col>
                <Col className="text-right">
                    <CancelButton color="light" className="border mx-1" disabled={savingData} ShowDropdown={resourceIsDirty} Close={() => {
                        HideDetails();
                        if (Close && typeof Close === 'function') {
                            Close();
                        }
                    }}>Cancel</CancelButton>
                    <Lock
                        unlocked={() =>
                            <Button color="success" disabled={!resourceIsDirty || savingData} onClick={() => {
                                saveResource(resource.id, () => {
                                    ReloadResource(() => {
                                        setEditResource(false);
                                        setResourceIsDirty(false);
                                        if (HideDetails && typeof HideDetails === 'function') {
                                            HideDetails();
                                        }
                                    });
                                });
                            }}>Save</Button>
                        }
                    />
                </Col>
            </Row>
            {!(!!resource.evictionStatusId || !!resource.declarationStatusId || (resource.eraPrograms && resource.eraPrograms.length > 0)) &&
                <div className="d-flex">
                    <div>
                        <FontAwesomeIcon className="text-secondary" size="2x" icon={faExclamationCircle} />
                    </div>
                    <div className="ml-2 flex-fill">
                        <span>Please provide the information requested for each lease where any leaseholder falls into one or more of these categories:</span>
                        <ol>
                            <li>Eviction has been filed in court against the leaseholder</li>
                            <li>Leaseholder provided a CDC declaration</li>
                            <li>Leaseholder or Community applied for emergency rental assistance</li>
                        </ol>
                        <span>Respond for any current or former leaseholder for any of the above categories since 9/1/20</span>
                    </div>
                </div>
            }
            <Row>
                <Col sm="3">
                    <Field type="text" field="residentName" fieldClassName={resource.residentName ? '' : 'is-invalid'} value={resource.residentName} label="Primary Leaseholder" EnableInput={{ disabled: !editResource }} onChange={(e) => updateResource(e.target.name, e.target.value, resource)} />
                </Col>
                <Col sm="3">
                    <Field type="select" field="residentStatus" fieldClassName={resource.residentStatus ? '' : 'is-invalid'} value={resource.residentStatus} label="Lease Status" EnableInput={{ disabled: !editResource }} onChange={(e) => updateResource(e.target.name, e.target.value, resource)}>
                        <option>Select a Resident Status</option>
                        {residentStatuses.map(option => <option key={option} value={option}>{option}</option>)}
                    </Field>
                </Col>
                <Col>
                    <Field type="text" field="unit" fieldClassName={resource.unit ? '' : 'is-invalid'} value={resource.unit} label="Unit" EnableInput={{ disabled: !editResource }} onChange={(e) => updateResource(e.target.name, e.target.value, resource)} />
                </Col>
                <Col>
                    <Field type="text" field="leaseId" value={resource.leaseId} label="Lease Id" help={resource.residentName ? `Resident/Lease ID for ${resource.residentName}` : 'Resident/Lease ID'} EnableInput={{ disabled: !editResource }} onChange={(e) => updateResource(e.target.name, e.target.value, resource)} />
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="border rounded p-2 mb-3">
                        <fieldset>
                            <legend className="border-bottom">Eviction Details</legend>
                            <Row>
                                <Col sm="6" md="4" xl="3">
                                    <Field type="select" fieldClassName={!!resource.evictionStatusId || !!resource.declarationStatusId || (resource.eraPrograms && resource.eraPrograms.length > 0) ? '' : 'is-invalid'} field="evictionStatusId" value={resource.evictionStatusId} label="Status" EnableInput={{ disabled: !editResource }} onChange={(e) => updateResource(e.target.name, e.target.value, resource)}>
                                        <option value="">Not Applicable</option>
                                        {(evictionStatuses || []).map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
                                    </Field>
                                </Col>
                                {resource.evictionStatusId && !(GetOptionText(evictionStatuses, resource.evictionStatusId, 'id', 'name') || '').includes('Unknown') &&
                                    <Col>
                                        <Field type="select" field="evictionSubstatusId" value={resource.evictionSubstatusId} label="Result" EnableInput={{ disabled: !editResource }} onChange={(e) => updateResource(e.target.name, e.target.value, resource)}>
                                            <option value="">Select a Result</option>
                                            {(evictionStatuses.find(status => status.id === resource.evictionStatusId).substatuses || []).sort((a, b) => a.order - b.order).map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
                                        </Field>
                                    </Col>
                                }
                                <Col md="3">
                                    {resource.evictionStatusId &&
                                        <Field type="select" field="reasonId" value={resource.reasonId} label="Reason" EnableInput={{ disabled: !editResource }} onChange={(e) => updateResource(e.target.name, e.target.value, resource)}>
                                            <option value="">Select an Eviction Reason</option>
                                            {(reasons || []).map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
                                        </Field>
                                    }
                                </Col>
                            </Row>
                            {resource.evictionStatusId &&
                                <div>
                                    <Row>
                                        <Col sm="6" md="4" lg="3">
                                            <Field type="date" field="filingDate" minDate={filingDateValidation.minimumDate} maxDate={filingDateValidation.maximumDate} fieldClassName={(!(resource.filingDate || filingDateValidation.requiredForStatuses.includes(resource.evictionStatusId)) || (new Date(resource.filingDate) >= new Date(filingDateValidation.minimumDate) && new Date(resource.filingDate) <= new Date(filingDateValidation.maximumDate))) ? '' : 'is-invalid'} value={resource.filingDate ? new Date(resource.filingDate) : null} help={`Since ${filingDateValidation.minimumDate}`} label="Filing Date" EnableInput={{ disabled: !editResource }} onChange={(value) => updateResource('filingDate', value, resource)} />
                                        </Col>
                                    </Row>
                                </div>
                            }
                        </fieldset>
                    </div>
                    <div className="border rounded p-2 mb-3">
                        <fieldset>
                            <legend className="border-bottom">CDC Declaration</legend>
                            <Row>
                                <Col sm="6" md="4" xl="3">
                                    <Field type="select" fieldClassName={!!resource.evictionStatusId || !!resource.declarationStatusId || (resource.eraPrograms && resource.eraPrograms.length > 0) ? '' : 'is-invalid'} field="declarationStatusId" value={resource.declarationStatusId} label="Status" EnableInput={{ disabled: !editResource }} HideHelp onChange={(e) => updateResource(e.target.name, e.target.value, resource)}>
                                        <option value="">Not Applicable</option>
                                        {(declarationStatuses || []).map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
                                    </Field>
                                </Col>
                            </Row>
                        </fieldset>
                    </div>
                    <div className="border rounded p-2">
                        <fieldset>
                            <legend className="border-bottom">Emergency Rental Assistance {eraPrograms && eraPrograms.length > 0 && editResource &&
                                <Button color="success" size="sm" outline onClick={addProgram}><FontAwesomeIcon icon={faPlus} /></Button>
                            }</legend>
                            {eraPrograms && eraPrograms.length > 0 ?
                                <div>
                                    <Program Header />
                                    <div className="border rounded scrollable-list p-1 pr-2">
                                        {resource.eraPrograms && resource.eraPrograms.length > 0 ?
                                            (resource.eraPrograms.map((program, index) =>
                                                <Program key={`${resource.id}_${index}`} {...{ program, eraPrograms, eraStatuses, updateProgram, index }}
                                                    RemoveProgram={() => removeProgram(index)}
                                                />
                                            ))
                                            :
                                            <div className={`text-dark rounded p-2`}>
                                                ERA is Not Applicable
                                        </div>
                                        }
                                    </div>
                                </div>
                                :
                                <div className={`text-dark rounded p-2`}>
                                    No ERA programs available
                                        </div>
                            }
                        </fieldset>
                    </div>
                </Col>
            </Row>
            {!newResource &&
                <div className="text-right">
                    <small>Updated By: <span className="text-dark">{resource.updatedBy}, {FuzzyDate(resource.updated, true, true, true)}</span></small>
                </div>
            }
        </div>
    );
};


// WEBPACK FOOTER //
// ./src/components/LeaseDetails/View.js